import React, { useState } from 'react';
import { Dialog, DialogContent, Button, Grid, Typography } from '@mui/material';
import { useTheme, useMediaQuery } from '@mui/material';
import GuardianImage from '../../../../assets/icon/Add_guardian.svg';
import CSVIcon from '../../../../assets/icon/CSV.svg';
import { useDropzone } from 'react-dropzone';
import { schoolViewModel } from '../school.view.model';
import Papa from 'papaparse';
import sampleCsv from '../../../../assets/csv/gaurdian.csv';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
const UploadCSVModal = ({ handleClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [errorBlobUrl, setErrorBlobUrl] = useState(null); // For error file blob URL
  const [uploadSuccess, setUploadSuccess] = useState(false); // Success state for uploading
  const [validateSuccess, setValidateSuccess] = useState(false);

  const onClose = () => handleClose();

  // Handling file drop
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: '.csv',
    onDrop: (acceptedFiles) => {
      setFile(acceptedFiles[0]);
      setErrorMessage(''); // Reset any previous errors
      setErrorBlobUrl(null); // Reset error blob on new file
      setUploadSuccess(false); // Reset upload success on new file
    },
  });

  // CSV validation function
  const validateCSV = (parsedData) => {
    const requiredFields = [
      'guardian_email',
      'guardian_first_name',
      'guardian_last_name',
      'child_first_name',
      'tribe_unique_id',
      'relationship_to_kid',
      'phone_number',
      'country_code',
    ];

    const errors = []; // Array to collect all errors

    for (let row of parsedData) {
      if (Object.values(row).every((value) => value === '')) continue;

      let rowErrors = []; // Collect errors for a specific row

      for (let field of requiredFields) {
        const value = row[field] ? row[field].trim() : '';
        if (!value) {
          rowErrors.push(`Missing data in field: ${field}`);
        }
      }
      for (let key in row) {
        if (!requiredFields.includes(key)) {
          rowErrors.push(`Unexpected field found: ${key}`);
          delete row[key]; // Remove the extra field
        }
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(row.guardian_email.trim())) {
        rowErrors.push(`Invalid email format: ${row.guardian_email}`);
      }

      let phoneNumberStr = row.phone_number;
      const countryCode = row.country_code;

      // Automatically prepend "+" if missing
      if (!phoneNumberStr.startsWith('+')) {
        phoneNumberStr = `+${phoneNumberStr}`;
      }

      if (/\s/.test(phoneNumberStr)) {
        rowErrors.push(
          `Phone number contains unnecessary spaces: ${row.phone_number} or check space between calling code and phone number`
        );
      } else {
        const phoneNumber = parsePhoneNumberFromString(phoneNumberStr);

        if (!phoneNumber || !phoneNumber.isValid()) {
          rowErrors.push(`Invalid phone number format: ${row.phone_number}`);
        } else {
          if (phoneNumber.country !== countryCode) {
            rowErrors.push(
              `Phone number does not match the country code: ${row.phone_number} (expected for ${countryCode})`
            );
          }
        }
      }

      // If the row has any errors, push it to the errors array
      if (rowErrors.length > 0) {
        errors.push({ ...row, errors: rowErrors.join('; ') });
      }
    }

    // If there are errors, generate a CSV file
    if (errors.length > 0) {
      const csvData = [
        'guardian_email,guardian_first_name,guardian_last_name,child_first_name,tribe_unique_id,relationship_to_kid,phone_number,country_code,errors',
      ];
      errors.forEach((errorRow) => {
        const rowString = `${errorRow.guardian_email},${errorRow.guardian_first_name},${errorRow.guardian_last_name},${errorRow.child_first_name},${errorRow.tribe_unique_id},${errorRow.relationship_to_kid},${errorRow.phone_number},${errorRow.country_code},"${errorRow.errors}"`;
        csvData.push(rowString);
      });

      // Generate Blob URL for the CSV file
      const csvContent = csvData.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const validationURL = URL.createObjectURL(blob);

      // Return the Blob URL
      return validationURL;
    }

    return null; // No errors
  };

  // Function to validate and upload the file
  const handleValidate = async () => {
    if (!file) return;

    setLoading(true);
    setErrorMessage('');
    setErrorBlobUrl(null); // Clear error blob before validation

    Papa.parse(file, {
      header: true,
      complete: async (results) => {
        // console.log(results,'resultsresults')
        const parsedData = results.data;
        const blobUrl = validateCSV(parsedData);
        if (blobUrl) {
          // console.log(validationError,'validationError')
          setErrorBlobUrl(blobUrl)
          setLoading(false);
          return;
        }

        // Proceed with validating the file with the backend API
        try {
          const res = await schoolViewModel.validateFile(file);
          if (res.status === 'Validated') {
            setValidateSuccess(true); // File is validated successfully, now allow upload
          } else {
            setErrorBlobUrl(res); // Store the blob URL for error download
          }
        } catch (err) {
          console.log(err);
          setErrorMessage('Failed to validate file.');
        } finally {
          setLoading(false);
        }
      },
      error: () => {
        setErrorMessage('Failed to parse CSV file');
        setLoading(false);
      },
    });
  };

  // Function to upload the file after successful validation
  const handleUpload = async () => {
    setLoading(true);
    try {
      const res = await schoolViewModel.guardianInviteBulkUpload(file);
      if (res) {
        setUploadSuccess(true);
      } else {
        setErrorMessage('Failed to upload file');
      }
    } catch (err) {
      setErrorMessage('An error occurred during file upload.');
    } finally {
      setLoading(false);
      setValidateSuccess(false);
    }
  };
  // console.log(errorBlobUrl,'errorBlobUrlerrorBlobUrl')
  return (
    <Grid container direction="column" alignItems="center" spacing={3}>
      <Grid item>
        <img
          src={GuardianImage}
          alt="Add Guardian"
          style={{
            width: isMobile ? '150px' : '200px',
            height: 'auto',
            marginBottom: '10px',
          }}
        />
      </Grid>

      <Grid item>
        <Typography
          variant="h6"
          align="center"
          fontWeight="bold"
          color="#321b6d"
        >
          Upload CSV
        </Typography>
      </Grid>

      <Grid item container justifyContent="center">
        <div
          {...getRootProps()}
          style={{
            width: '100%',
            border: '2px dashed #bcbcbc',
            borderRadius: '8px',
            padding: '20px',
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: isDragActive ? '#f0f0f0' : '#fff',
          }}
        >
          <input {...getInputProps()} />
          <img
            src={CSVIcon}
            alt="CSV Icon"
            style={{ marginBottom: '8px', width: '40px' }}
          />
          <Typography variant="body1" style={{ color: '#321b6d' }}>
            {file
              ? file.name
              : 'Select a CSV file to Import or drag and drop it here'}
          </Typography>
        </div>
      </Grid>

      {/* Error or success messages */}
      {errorMessage && (
        <Grid item>
          <Typography variant="body2" color="error">
            {errorMessage}
          </Typography>
        </Grid>
      )}
      {uploadSuccess && (
        <Grid item>
          <Typography variant="body2" color="success">
            File uploaded successfully!
          </Typography>
        </Grid>
      )}
      {validateSuccess && (
        <Grid item>
          <Typography variant="body2" color="success">
            File validated successfully!
          </Typography>
        </Grid>
      )}

      {/* Error file download */}
      {errorBlobUrl && (
        <>
          <Grid item>
            <Button
              variant="outlined"
              sx={{
                width: '100%',
                borderRadius: '25px',
                borderColor: 'red', // Red outline color
                color: 'red', // Red text color
                '&:hover': {
                  borderColor: 'darkred', // Darker red on hover (optional)
                },
              }}
              href={errorBlobUrl}
              download="error_file.csv"
            >
              Download Error File
            </Button>
          </Grid>
          <Grid item sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="error">
              There are some errors in the file. Please download the error file
              by clicking the button above, correct the errors, and upload the
              file again.
            </Typography>
          </Grid>
        </>
      )}

      {/* Download sample CSV button */}
      {!errorBlobUrl && (
        <Grid item>
          <Button
            variant="outlined"
            sx={{ width: '100%', borderRadius: '25px' }}
            href={sampleCsv}
            download
          >
            Download Sample CSV
          </Button>
        </Grid>
      )}

      {/* Upload button will only appear if there are no errors and the file is successfully validated */}
      {validateSuccess && !errorBlobUrl && (
        <Grid item>
          <Button
            variant="contained"
            disabled={!file || loading}
            sx={{
              width: isMobile ? '100%' : '150px',
              borderRadius: '25px',
            }}
            onClick={handleUpload}
            className="gred-btn"
          >
            {loading ? 'Processing...' : 'Upload'}
          </Button>
        </Grid>
      )}

      {/* Validate button */}
      {!validateSuccess && !errorBlobUrl && (
        <Grid item>
          <Button
            variant="contained"
            disabled={!file || loading}
            sx={{
              width: isMobile ? '100%' : '150px',
              borderRadius: '25px',
            }}
            onClick={handleValidate}
            className="gred-btn"
          >
            {loading ? 'Processing...' : 'Validate'}
          </Button>
        </Grid>
      )}
    </Grid>
  );
};

export default UploadCSVModal;
